<template>
  <b-modal
    v-model="showModal"
    size="md"
    centered
    cancel-title="Cancel"
    @show="handleShow"
  >
    <template v-slot:modal-title>
      {{ title }}
    </template>
    <b-row>
      <b-col cols="12">
        <div v-if="!editing && hasNotes" class="notes-container">
          <b-list-group>
            <b-list-group-item v-if="hasNotes && !loadingNotes">
              <div class="d-flex w-100 justify-content-between float-right">
                <small>{{ currentNoteCreatedDate }}</small>
              </div>
              <p class="mb-1">{{ currentNoteText }}</p>
            </b-list-group-item>
            <b-list-group-item v-for="nh in notesHistory" :key="nh.id">
              <div class="d-flex w-100 justify-content-between">
                <small>{{ noteHistoryCreatedDate(nh) }}</small>
              </div>
              <p class="mb-1">{{ noteHistoryText(nh) }}</p>
            </b-list-group-item>
          </b-list-group>
        </div>
        
        <b-form v-if="editing" @submit.stop.prevent="onSubmit">
          <b-form-group>
            <b-textarea :class="{ 'is-invalid' : apiErrors && apiErrors.length, 'disabled' : saving }"
                        v-model="newNote" maxlength="1000" placeholder="Enter note">
            </b-textarea>
            <validation-errors :validation-errors="apiErrors"/>
          </b-form-group>

          <b-button type="submit" variant="primary">Save</b-button>
          <b-button type="reset" @click="hideEditor">Cancel</b-button>
                  
          <form-success :success="success" class="form-alert">Note added!</form-success>
        </b-form>
        <br>
        <b-list-group>
          <b-list-group-item v-if="loadingNotes || loadingHistory || saving || displayOnly" :variant="messageStyle" class="text-center">
            {{ requestMessage }}
          </b-list-group-item>
          <b-list-group-item v-if="!displayOnly && !loadingNotes && !editing" class="text-center" button variant="success" @click="showEditor">
            <i class="fas fa-plus"></i> Add Note
          </b-list-group-item>
        </b-list-group>
      </b-col>
    </b-row>

    <template v-slot:modal-footer>
      <div class="w-100">
        <b-button
          variant="primary"
          size="sm"
          class="float-right"
          @click="showModal=false"
        >
          Close
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import formSuccess from '../../../common/form/form-success'
import formWrapper from '../../../common/form/form-wrapper'
import notesApi from '../../../../api/bioreq-notes'
import validationErrors from '@/components/common/form/validation-errors'
import {mapState} from 'vuex'
import { readableDate } from '@/helpers'

export default {
  components: {
    formSuccess,
    formWrapper,
    validationErrors
  },
  props: {
    bioreqDocument: {
      type: Object,
      required: true,
      default: null
    },
    displayOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      showModal: false,
      note: null,
      notesHistory: null,
      hasNotes: false,
      apiErrors: [],
      newNote: '',
      saving: false,
      loadingNotes: false,
      loadingHistory: false,
      success: false,
      editing: false,
      selected: '',
      requestMessage: '',
      messageStyle: 'warning'
    }
  },
  computed: {  
    disabled () {
      return this.errors.any() || this.saving
    },
    title () {
      return `Notes - ${this.bioreqDocument.title}`
    },
    ...mapState('tenants', {
      organization: 'current'
    }),
    currentNoteCreatedDate () {
      let date = ''
      
      if (this.note) {
        date = readableDate(this.note.created_at)
      }
      
      return date
    },
    currentNoteText () {
      let text = ''

      if (this.note) {
        text = this.note.note
      }

      return text
    }
  },
  watch: {
    showModal (newState, oldState) {
      if (newState === false) {
        this.$emit('hidden')
      }
    },
    success (newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.errors.clear()
        this.$store.commit('bioreq/forms/notes/resetFields')
      }
    }
  },
  mounted () {
    return this.bioreqDocument.id
  },
  methods: {
    readableDate,
    async openModal () {
      this.loadingNotes = true
      this.showModal = true

      const waitForBioreq = setInterval(async () => {
        if (this.bioreqDocument !== null) {
          clearInterval(waitForBioreq)

          await this.loadNote()
        }
      }, 100)
    },
    async onSubmit () {
      let success = false
      this.messageStyle = 'warning'
      this.requestMessage = 'Saving note...'
      this.saving = true

      if (this.newNote.length < 10 || this.newNote.length > 1000) {
        this.messageStyle = 'danger'
        this.requestMessage = 'Notes must be at least ten characters and no more than 1000 characters'
      } else {
        if (this.note) {
          const params = {tenantID: this.organization.id, bioreqID: this.bioreqDocument.id, noteID: this.note.id, data: {'note': this.newNote}}

          const response = await notesApi.update(params)

          if (response.status == 200) {
            success = true
          }
        } else {
          const params = {tenantID: this.organization.id, bioreqID: this.bioreqDocument.id, data: {'note': this.newNote}}

          const response = await notesApi.store(params)

          if (response.status == 201) {
            success = true
          }
        }

        if (success) {
          this.editing = false

          await this.loadNote()
        }
        
        this.saving = false
      }
    },
    async loadNote () {
      const params = {tenantID: this.organization.id, bioreqID: this.bioreqDocument.id}

      this.requestMessage = 'Loading notes...'
      this.loadingNotes = true
      const response = await notesApi.index(params)
      this.loadingNotes = false

      if (response.status == 200) {
        this.note = response.data.data[0]
        if (this.note) {
          this.hasNotes = true

          this.requestMessage = 'Loading notes history...'
          this.loadingHistory = true
          await this.loadNotesHistory()
          this.loadingHistory = false
        } else {
          this.messageStyle = 'outline-secondary'
          this.requestMessage = 'No notes found'
        }
      } else {
        this.apiErrors.push({'error': `${response.status} - There was an error while loading the notes`})
      }    
    },
    async loadNotesHistory () {
      this.loadingHistory = true
      const params = {tenantID : this.organization.id, bioreqID: this.bioreqDocument.id, noteID: this.note.id}

      const response = await notesApi.history(params)
      
      if (response.status == 200) {
        this.notesHistory = response.data.data
      } else {
        this.apiErrors.push({'error': `${response.status} - There was an error while loading the notes history`})
      }

      this.loadingHistory = false
    },
    noteHistoryText (noteHistory) {
      let text = ''

      if (noteHistory.note) {
        text = noteHistory.note
      }

      return text
    },
    noteHistoryCreatedDate (noteHistory) {
      let date = ''

      if (noteHistory.created_at) {
        date = readableDate(noteHistory.created_at)
      }

      return date
    },
    showEditor () {
      this.editing = true
      this.newNote = ''
    },
    hideEditor () {
      this.saving = false
      this.editing = false
    },
    handleShow () {
      this.note = null
      this.notesHistory = null
      this.newNote = '',
      this.saving = false,
      this.editing = false,
      this.loadingNotes = false,
      this.loadingHistory = false,
      this.success = false,
      this.messageStyle = 'warning'
    }
  }
}
</script>

<style scoped>
  .notes-container {
    height: 340px;
    overflow-y: auto;
  }
</style>


// WEBPACK FOOTER //
// src/components/pages/client/bioreq/notesModal.vue